import type React from "react"
import { useEffect, useMemo, useState } from "react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Loader, Search, X } from "lucide-react"
import { Textarea } from "../../../components/ui/textarea"
import { Link, useParams } from "react-router-dom"
import useSWR from "swr"
import axiosConfig, { handleMutation, fetcher } from "../../../util/axiosConfig"
import useSWRMutation from "swr/mutation"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { message, Popconfirm } from "antd"
import PageSpinner from "../../../components/PageSpinner"
import Grid from "../../../componentsV2/Icon/Grid"
import BreadCrumb from "../../../componentsV2/BreadCrumb"
import { API_ENDPOINTS } from "../../../util/api"
import dayjs from "dayjs"
import ZoneBoundsMap from "../../../componentsV2/ZoneBoundsMap"
import requestErrorHandler from "../../../util/requestErrorHandler"

/** Default bounds pre-loaded for new zones (Lagos, Nigeria) */
const DEFAULT_NE: LatLng = { latitude: "6.6500", longitude: "3.5000" };
const DEFAULT_SW: LatLng = { latitude: "6.4000", longitude: "3.2500" };

type ZoneFormState = {
  title?: string;
  type?: string;
  vehicleCategoryId: string;
  description?: string;
  northEastBound?: LatLng;
  southWestBound?: LatLng;
  id?: string;
};

export default function TruckZones() {
  const { truckId } = useParams();
  const [showAddZonePanel, setShowAddZonePanel] = useState(false);
  const [selectedZone, setSelectedZone] = useState<Zone>();
  const [search, setSearch] = useState("");
  const { trigger, isMutating } = useSWRMutation(API_ENDPOINTS.ZONES.CREATE, handleMutation);
  const [isUpdating, setIsUpdating] = useState(false);
  const { data: categoryData, isLoading: isLoadingCategory, mutate: mutateCategory } = useSWR(
    API_ENDPOINTS.TRUCK_CATEGORY.GET_ONE(truckId!),
    fetcher
  );

  const truckCategory: ZoneTrucks = categoryData?.truckCategory;

  const filteredZones: Zone[] = useMemo(() => {
    if (truckCategory?.zones) {
      return truckCategory.zones.filter((zone) =>
        zone.title.toLowerCase().includes(search.toLowerCase())
      );
    }
    return [];
  }, [truckCategory, search]);

  const [newZone, setNewZone] = useState<ZoneFormState>({ vehicleCategoryId: truckId! });
  const [zoneAddress, setZoneAddress] = useState({ northEastAddress: "", southWestAddress: "" });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewZone((prev) => ({ ...prev, [name]: value }));
  };

  const handleBoundChange = (
    bound: "northEastBound" | "southWestBound",
    field: "latitude" | "longitude",
    value: string
  ) => {
    setNewZone((prev) => ({
      ...prev,
      [bound]: { ...(prev[bound] ?? {}), [field]: value },
    }));
  };

  const handleGeolocate = async (bound: LatLng, field: "northEastAddress" | "southWestAddress") => {
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat: parseFloat(bound.latitude), lng: parseFloat(bound.longitude) };
    try {
      const response = await geocoder.geocode({ location: latlng });
      if (response.results[0]) {
        setZoneAddress((prev) => ({ ...prev, [field]: response.results[0].formatted_address }));
      }
    } catch (_) {}
  };

  useEffect(() => {
    if (newZone.northEastBound?.latitude && newZone.northEastBound?.longitude) {
      handleGeolocate(newZone.northEastBound as LatLng, "northEastAddress");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newZone.northEastBound]);

  useEffect(() => {
    if (newZone.southWestBound?.latitude && newZone.southWestBound?.longitude) {
      handleGeolocate(newZone.southWestBound as LatLng, "southWestAddress");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newZone.southWestBound]);

  const handleAddZone = () => {
    setSelectedZone(undefined);
    setZoneAddress({ northEastAddress: "", southWestAddress: "" });
    setNewZone({
      vehicleCategoryId: truckId!,
      northEastBound: DEFAULT_NE,
      southWestBound: DEFAULT_SW,
    });
    setShowAddZonePanel(true);
  };

  const handleEditZone = (zone: Zone) => {
    setSelectedZone(zone);
    setZoneAddress({ northEastAddress: "", southWestAddress: "" });
    setNewZone({ ...zone, vehicleCategoryId: truckId! });
    setShowAddZonePanel(true);
  };

  const handleClosePanel = () => {
    setShowAddZonePanel(false);
    setSelectedZone(undefined);
  };

  const handleSaveZone = async (type: "save" | "update" | "delete", zoneId?: string) => {
    setIsUpdating(true);
    try {
      if (type === "delete") {
        await axiosConfig.delete(`zone/delete-zone?zoneId=${zoneId}`);
        message.success("Zone deleted successfully");
        mutateCategory();
        return;
      }

      if (type === "update") {
        await axiosConfig.put("zone/update", { ...newZone, zoneId: selectedZone?.id });
        message.success("Zone updated successfully");
        mutateCategory();
        handleClosePanel();
        return;
      }

      // Create — validate first
      if (
        !newZone.title?.trim() ||
        !newZone.type?.trim() ||
        !newZone.description?.trim() ||
        !newZone.northEastBound?.latitude ||
        !newZone.northEastBound?.longitude ||
        !newZone.southWestBound?.latitude ||
        !newZone.southWestBound?.longitude
      ) {
        message.error("Please fill in all required fields.");
        return;
      }

      await trigger({ ...newZone });
      message.success("Zone created successfully");
      mutateCategory();
      handleClosePanel();
    } catch (error) {
      requestErrorHandler(error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoadingCategory) {
    return <PageSpinner />;
  }

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 flex flex-col">
        <main className="flex-1 p-0 overflow-auto">
          <div className="flex h-full gap-4">

            {/* Zone Table */}
            <div
              className={`bg-white rounded-lg shadow p-4 mb-8 transition-all duration-300 ${
                showAddZonePanel ? "w-1/2" : "w-full"
              }`}
            >
              <BreadCrumb
                label={`Zone Configuration — ${truckCategory?.name}`}
                showBack
                right={
                  <div className="flex items-center gap-4">
                    {truckCategory?.zones.length > 0 && (
                      <Link to={`/admin/zones/map-zone-pricing/${truckId}`} className="bg-transparent border-none">
                        <Grid />
                      </Link>
                    )}
                    <Button className="bg-primary hover:bg-blue-700 text-white" onClick={handleAddZone}>
                      Add Zone
                    </Button>
                  </div>
                }
              />

              <div className="flex items-center mb-6">
                <div
                  className="flex items-center gap-2 rounded px-2"
                  style={{ border: "1px solid #d1d5db" }}
                >
                  <Search size={18} className="text-gray-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search zones"
                    className="bg-white shadow-none"
                  />
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Zone Title</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">Date Created</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredZones.map((zone: Zone) => (
                    <TableRow key={zone.id}>
                      <TableCell className="font-medium">{zone.title}</TableCell>
                      <TableCell className="text-primary capitalize">{zone.type}</TableCell>
                      <TableCell>{dayjs(zone.createdAt).format("DD/MM/YYYY")}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => handleEditZone(zone)}
                            className="bg-primary hover:bg-blue-700 text-white"
                          >
                            Edit
                          </Button>
                          <Popconfirm
                            title="Delete this zone?"
                            description="This action cannot be undone."
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true, type: "default" }}
                            cancelButtonProps={{ type: "primary" }}
                            onConfirm={() => handleSaveZone("delete", zone.id)}
                          >
                            <Button className="bg-red-500 hover:bg-red-600 text-white">Delete</Button>
                          </Popconfirm>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {filteredZones.length === 0 && (
                <div className="text-gray-400 text-sm w-full flex justify-center items-center mt-10">
                  {search
                    ? "No zones match your search."
                    : "No zones have been added to this category."}
                </div>
              )}
            </div>

            {/* Add / Edit Zone Panel */}
            {showAddZonePanel && (
              <div
                key={selectedZone?.id ?? "new"}
                className="bg-white rounded-lg shadow flex flex-col w-1/2 mb-8 transition-all duration-300 overflow-hidden"
              >
                {/* Header */}
                <div className="flex justify-between items-center px-4 py-3 border-b flex-shrink-0">
                  <h2 className="text-lg font-bold text-primary">
                    {selectedZone ? "Edit Zone" : "Add Zone"}
                  </h2>
                  <Button variant="ghost" size="icon" onClick={handleClosePanel}>
                    <X className="h-5 w-5 text-red-500" />
                  </Button>
                </div>

                {/* Form Fields */}
                <div className="px-4 pt-3 pb-2 space-y-3 flex-shrink-0">
                  <Input
                    name="title"
                    value={newZone.title ?? ""}
                    onChange={handleInputChange}
                    placeholder="Zone Title"
                    className="bg-gray-100"
                  />

                  <Select
                    value={newZone.type}
                    onValueChange={(val: string) => setNewZone((prev) => ({ ...prev, type: val }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Zone Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="quarry">Quarry</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                    </SelectContent>
                  </Select>

                  <Textarea
                    name="description"
                    value={newZone.description ?? ""}
                    onChange={handleInputChange}
                    placeholder="Description"
                    className="bg-gray-100 resize-none h-16"
                  />

                  {/* NE Bound */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">
                      NE Bound
                    </label>
                    <Input
                      value={zoneAddress.northEastAddress}
                      readOnly
                      placeholder="Address updates after dragging pin"
                      className="bg-gray-50 text-xs mb-1"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={newZone.northEastBound?.latitude ?? ""}
                        onChange={(e) => handleBoundChange("northEastBound", "latitude", e.target.value)}
                        placeholder="Latitude"
                        className="bg-gray-100 text-xs"
                      />
                      <Input
                        value={newZone.northEastBound?.longitude ?? ""}
                        onChange={(e) => handleBoundChange("northEastBound", "longitude", e.target.value)}
                        placeholder="Longitude"
                        className="bg-gray-100 text-xs"
                      />
                    </div>
                  </div>

                  {/* SW Bound */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">
                      SW Bound
                    </label>
                    <Input
                      value={zoneAddress.southWestAddress}
                      readOnly
                      placeholder="Address updates after dragging pin"
                      className="bg-gray-50 text-xs mb-1"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={newZone.southWestBound?.latitude ?? ""}
                        onChange={(e) => handleBoundChange("southWestBound", "latitude", e.target.value)}
                        placeholder="Latitude"
                        className="bg-gray-100 text-xs"
                      />
                      <Input
                        value={newZone.southWestBound?.longitude ?? ""}
                        onChange={(e) => handleBoundChange("southWestBound", "longitude", e.target.value)}
                        placeholder="Longitude"
                        className="bg-gray-100 text-xs"
                      />
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-400 leading-relaxed">
                    Drag the{" "}
                    <span className="text-blue-600 font-semibold">NE</span> (blue) and{" "}
                    <span className="text-orange-500 font-semibold">SW</span> (orange) pins on the
                    map, or type coordinates directly.
                  </p>
                </div>

                {/* Map — fills remaining height */}
                <div className="flex-1 min-h-[240px] px-4 pb-2">
                  <ZoneBoundsMap
                    northEastBound={newZone.northEastBound}
                    southWestBound={newZone.southWestBound}
                    onNEChange={(latLng) =>
                      setNewZone((prev) => ({ ...prev, northEastBound: latLng }))
                    }
                    onSWChange={(latLng) =>
                      setNewZone((prev) => ({ ...prev, southWestBound: latLng }))
                    }
                  />
                </div>

                {/* Save Button */}
                <div className="px-4 py-3 border-t flex-shrink-0">
                  <Button
                    disabled={isMutating || isUpdating}
                    className="w-full bg-primary hover:bg-blue-700 text-white"
                    onClick={() => handleSaveZone(selectedZone ? "update" : "save")}
                  >
                    {isMutating || isUpdating ? (
                      <Loader className="animate-spin" />
                    ) : selectedZone ? (
                      "Save Changes"
                    ) : (
                      "Save Zone"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

